<template>
  <modal title="Post" ref="modal">
    <loader :task="task" />
    <post v-if="post" :post="post" trash actions />
    <post-notes v-if="post" :post-id="post.id" class="q-mt-md" />
  </modal>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import eventBus from '@/messaging/event-bus';
import {
  PostEvent,
  PostSaved,
  PostEscalated,
  PostResolved,
  PostMovedToTimeline,
  PostRemovedFromTimeline,
  PostTrashed,
  PostRestored,
} from '@/messaging/events';
import { StringMatchRule } from '@/util/string';
import { ModalMixin } from '@/mixins';
import { Post as PostType } from '@/type';
import Modal from '@/components/modal/Modal.vue';
import Loader from '@/components/loading/AsyncLinearProgress.vue';
import PostNotes from './note/PostNotes.vue';
import repository from './post-repository';
import Post from './card/Post.vue';

interface Data {
  task: Promise<PostType>|null,
  post: PostType|null,
}

export default defineComponent({
  mixins: [ModalMixin],
  components: {
    Modal,
    Loader,
    Post,
    PostNotes,
  },
  props: {
    postId: {
      type: Number,
      required: true,
    },
    highlight: {
      type: Array as () => StringMatchRule[],
      default: () => [],
    },
  },
  data(): Data {
    return {
      task: null,
      post: null,
    };
  },
  watch: {
    postId: {
      handler() {
        this.load();
      },
      immediate: true,
    },
  },
  methods: {
    load(): void {
      this.task = repository.find(this.postId);
      this.task.then((post: PostType) => {
        this.post = post;
      });
    },
    onPostChanged(event: PostEvent): void {
      if (this.post && this.post.id === event.post.id) {
        this.post = event.post;
      }
    },
  },
  created(): void {
    eventBus.$on(PostSaved.eventName, this.onPostChanged);
    eventBus.$on(PostEscalated.eventName, this.onPostChanged);
    eventBus.$on(PostResolved.eventName, this.onPostChanged);
    eventBus.$on(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$on(PostRemovedFromTimeline.eventName, this.onPostChanged);
    eventBus.$on(PostTrashed.eventName, this.onPostChanged);
    eventBus.$on(PostRestored.eventName, this.onPostChanged);
  },
  beforeUnmount(): void {
    eventBus.$off(PostSaved.eventName, this.onPostChanged);
    eventBus.$off(PostEscalated.eventName, this.onPostChanged);
    eventBus.$off(PostResolved.eventName, this.onPostChanged);
    eventBus.$off(PostMovedToTimeline.eventName, this.onPostChanged);
    eventBus.$off(PostRemovedFromTimeline.eventName, this.onPostChanged);
    eventBus.$off(PostTrashed.eventName, this.onPostChanged);
    eventBus.$off(PostRestored.eventName, this.onPostChanged);
  },
});
</script>
